import clsx from 'clsx';
import { TAS_BACK_END_API_URL, URL_TAS_VERSION } from '../../../../constants';
import { fetchAuthenticated } from '../../../../controllers';
import { useCallback, useContext, useEffect, useState } from 'react';
import './PatientBloodSamples.scss';
import { TextInput } from '../../../../components/TextInput/TextInput';
import { Button } from '../../../../components/Button/Button';
import { Plus } from '../../../../shared/assets/plus';
import { Header } from '../../../../components/common/table/Header/Header';
import { HeaderCell } from '../../../../components/common/table/HeaderCell/HeaderCell';
import { FilterButton } from '../../../../components/common/buttons/iconButtons/FilterButton';
import { SampleHistoryRow } from './SampleHistoryRow/SampleHistoryRow';
import { UserProps } from '../../../../shared/types/types';
import { encryptObject, decryptObject } from '../../../../components/Encryption/obfuscationHandler';
import { v4 } from 'uuid';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { PopupMessageContext } from 'src/shared/contexts/PopupMessageContext';
import { Card } from 'src/components/Card/Card';
import { ExternalUserVerifyContext } from 'src/shared/contexts/ExternalAprrovalContext';
import { LoadingSpinner, mediumSpinner, smallSpinner } from 'src/components/common/loader/LoadingSpinner';
import { offWhite, primaryBlue400, silver } from 'src/shared/styles/variables';
import { RefreshIcon } from 'src/icons/Refresh';

interface PatientBloodSamplesProps {
  patientId?: string;
  medicalId?: string;
  token: string | null;
}

export interface BloodSample {
  _id: string;
  body: BodyProps;
  header: HeaderProps;
}
interface HeaderProps {
  item_id: string;
  version: string;
  timestamp_local: string;
  timestamp_utc: string;
  patient_id: string;
  user: UserProps;
}
interface BodyProps {
  draw_date_time_utc: string;
  encrypt_vial_id: string;
  archived: boolean;
  icp_oes_boron_concentration_ppm: number;
}

export interface BloodSamplesServiceResult {
  status: string;
  total: number;
  items: Array<BloodSample>;
  errors: string[];
}

export interface BloodSamplesAjaxResult {
  result: BloodSamplesServiceResult;
}

export const PatientBloodSamples = ({ patientId, medicalId, token }: PatientBloodSamplesProps) => {
  const [bloodSamples, setBloodSamples] = useState<BloodSample[] | null>(null);
  const { checkAuthorization } = useContext(ExternalUserVerifyContext);
  const [barcode, setBarcode] = useState<string>('');
  const [patientCode, setPatientCode] = useState<string>('');

  const [addDisabled, setAddDisabled] = useState<boolean>(false);
  const [status, setStatus] = useState<string | null>(null);
  const [expanded, setIsExpanded] = useState(true);
  const [refreshingBloodSamples, setRefreshingBloodSamples] = useState<boolean>(false);
  const [bloodSampleIsLoading, setBloodSampleIsLoading] = useState<boolean>(false);

  const handlePatientInfoExpand = () => {
    setIsExpanded(!expanded);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiData, setApiData] = useState<BloodSamplesAjaxResult>();
  const [serverError, setServerError] = useState();
  const { addBannerMessage } = useContext(BannerMessageContext);
  const { addPopupMessage } = useContext(PopupMessageContext);

  const getBloodSampleData = useCallback(async () => {
    const config: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    setIsLoading(true);
    const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientId}/blood-samples`;

    const response = await fetchAuthenticated<BloodSamplesAjaxResult>(url, config);

    try {
      if (response?.ok) {
        setApiData(response?.parsedBody);
      }
    } catch (error) {
      setIsLoading(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Uh oh! There was an error getting the Blood Sample Data. Please refresh to try again.',
      });
    }
  }, [token, addBannerMessage, patientId]);

  useEffect(() => {
    try {
      getBloodSampleData();
    } catch (error: any) {
      setServerError(error);
    }
  }, [getBloodSampleData]);

  useEffect(() => {
    async function decryptData() {
      if (apiData?.result?.items) {
        try {
          await decryptObject(apiData.result, token, null);
          setBloodSamples(apiData?.result.items);
        } catch {
          setBloodSamples(null);
        }
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    }
    decryptData();
  }, [apiData, token]);

  const verifyPatientCodeClick = () => {
    if (medicalId === patientCode) {
      setAddDisabled(true);
      setBarcode('');
      setStatus(null);
      addBannerMessage({
        id: v4(),
        type: 'success',
        message: 'Patient Code Confirmed!',
      });
    } else {
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Patient Code Incorrect!',
      });
    }
  };

  const addPatientBloodSample = async () => {
    if (barcode.length < 8) {
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Barcode Length Invalid. Barcode must be between 8 and 10 alphanumeric characters.',
      });
      return;
    }

    setBloodSampleIsLoading(true);
    if (!checkAuthorization) {
      setBloodSampleIsLoading(false);
      return;
    }

    let result;
    try {
      result = await checkAuthorization();
    } catch (error) {
      setBloodSampleIsLoading(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
    }
    if (result?.verified) {
      async function decryptData() {
        if (barcode !== '') {
          setStatus('Uploading Data');
          setAddDisabled(!addDisabled);
          const body = {
            encrypt_vial_id: barcode,
            draw_date_time_utc: new Date().toISOString(),
          };

          const config: RequestInit = {
            method: 'PUT',
            body: JSON.stringify(await encryptObject(body, token, null)),
            headers: { Authorization: `Bearer ${token}` },
          };

          const putBloodSampleData = async () => {
            const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientId}/blood-samples`;

            const response = await fetchAuthenticated(url, config); // Sends a put request that uploads new barcode
            if (!response?.ok) {
              // share a failed response
              setAddDisabled(false);
              setStatus('Failed Upload');
              setBloodSampleIsLoading(false);
              setPatientCode('');
              addPopupMessage({
                id: v4(),
                message: 'There was an error adding the blood sample. Please retry request.',
                header: 'Addition',
              });
            } else {
              setAddDisabled(false);
              setStatus(null);
              setBarcode('');
              const res: any = await response.parsedBody;
              await decryptObject(res?.result, token, null);
              setBloodSamples([...(bloodSamples ?? []), res?.result?.items]);
              setBloodSampleIsLoading(false);
            }
          };
          putBloodSampleData();
        }
      }
      await decryptData();
      setPatientCode('');
      setBloodSampleIsLoading(false);
      setAddDisabled(!addDisabled);
    } else {
      setBloodSampleIsLoading(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
    }
  };

  const updateApiData = (patchedBloodSample: BloodSample) => {
    const newBloodSamples = [];
    if (!bloodSamples) return;
    const patchedBloodSampleItemId = patchedBloodSample?.header?.item_id;
    for (let i = 0; i < bloodSamples?.length; i++) {
      if (bloodSamples[i].header?.item_id !== patchedBloodSampleItemId) {
        newBloodSamples.push(bloodSamples[i]);
      } else {
        newBloodSamples.push(patchedBloodSample);
      }
    }
    setBloodSamples(newBloodSamples);
  };

  const handleRefreshBloodSamples = () => {
    setRefreshingBloodSamples(true);
    setTimeout(() => {
      getBloodSampleData();
      setRefreshingBloodSamples(false);
    }, 1500);
  };

  const body = (
    <div className="patient-blood-sample-container" data-testid="patient-blood-samples-card">
      <div className="add-blood-sample">
        <div className="blood-sample-input-container">
          <div style={{ paddingBottom: '3px' }}>Please Type or Scan the Patient Code</div>
          <TextInput value={patientCode} placeHolder={'Patient Code'} onChange={(e) => setPatientCode(e.target.value)} disabled={addDisabled} width={336} />
        </div>
        <div className="add-button-container">
          {
            <Button
              label="Verify Patient Code"
              icon={<Plus fill={addDisabled ? silver : offWhite} />}
              iconPlacement="left"
              width="192px"
              onClick={() => verifyPatientCodeClick()}
              className={clsx('add-button', !addDisabled && 'add-button-enabled')}
              disabled={addDisabled}
            />
          }
        </div>
      </div>
      <div className="add-blood-sample">
        <div className="blood-sample-input-container">
          <TextInput
            value={status || barcode}
            placeHolder={"Scan the vial's barcode"}
            onChange={(e) => setBarcode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
            disabled={!addDisabled || bloodSampleIsLoading}
            width={336}
          />
        </div>
        <div className="add-button-container">
          {
            <Button
              label={bloodSampleIsLoading ? '' : 'Add Blood Sample'}
              icon={bloodSampleIsLoading ? <LoadingSpinner width={smallSpinner} height={smallSpinner} color="white" /> : <Plus fill={!addDisabled ? silver : offWhite} />}
              iconPlacement="left"
              width="192px"
              onClick={() => addPatientBloodSample()}
              className={clsx('add-button', addDisabled && 'add-button-enabled')}
              disabled={!addDisabled}
            />
          }
        </div>
      </div>
      <>
        {serverError && <p>Error:</p>}
        {
          <table className="table-container">
            <Header>
              <HeaderCell>
                <div>vial id</div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div className="time-format-cell">
                  <div>boron concentration</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>¹⁰B μg/g</div>
                </div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div className="time-format-cell">
                  <div>draw date</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>dd:MMM:YYYY</div>
                </div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div className="time-format-cell">
                  <div>draw time</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>HH:mm:ss</div>
                </div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div>last updated</div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell align="center">
                <div style={{ paddingRight: '50px' }}>action</div>
              </HeaderCell>
              <HeaderCell align="center" onClick={() => handleRefreshBloodSamples()}>
                {!refreshingBloodSamples ? (
                  <>
                    <div>Refresh&nbsp;&nbsp;&nbsp;</div>
                    <RefreshIcon height={20} width={20} color={primaryBlue400} />
                  </>
                ) : (
                  <LoadingSpinner width={smallSpinner} height={smallSpinner} />
                )}
              </HeaderCell>
            </Header>
            <tbody className="body-container">
              {bloodSamples &&
                bloodSamples.length > 0 &&
                bloodSamples.map((sample, index) => (
                  <SampleHistoryRow
                    token={token}
                    sample={sample}
                    index={index}
                    patientId={patientId}
                    key={sample._id}
                    updateApiData={(data: BloodSample) => updateApiData(data)}
                  />
                ))}
              <div className="center-text">
                {isLoading && !bloodSamples && <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />}
                {!isLoading && bloodSamples && Array.isArray(bloodSamples) && bloodSamples.length === 0 && (
                  <>Please add blood samples by clicking on the white box above and then scanning the barcode on the vial</>
                )}
              </div>
            </tbody>
          </table>
        }
      </>
    </div>
  );
  return (
    <div className="patient-info-container">
      <Card title={'Blood Samples'} body={expanded ? body : <></>} onExpand={handlePatientInfoExpand} expandable={true} expanded={expanded} />
    </div>
  );
};
